import { useState, useEffect } from 'react';
import { FiClock, FiExternalLink, FiUsers } from 'react-icons/fi';
import { TbMoneybag } from "react-icons/tb";
import { parseEther } from 'viem';
import toast from 'react-hot-toast';
import { usePlaceBet } from '../../hooks/useContracts';

import Countdown from '../../../../shared/components/Countdown/Countdown';

interface PredictionOption {
  range: string;
}

interface Video {
  id: number;
  username: string;
  category: string;
  duration: string;
  currentViews: number;
  question: string;
  predictionOptions: PredictionOption[];
  totalPot: string;
  totalPredictions: number;
  closesIn: string;
  color: 'purple' | 'cyan' | 'yellow';
  videoUrl?: string;
  postUrl?: string;
  viewsDeadline?: bigint;
  bettingDeadline?: bigint;
}

interface VideoCardProps {
  video: Video;
  videoId: bigint;
  isBettingClosed?: boolean;
}

export default function VideoCard({ video, videoId, isBettingClosed = false }: VideoCardProps) {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [betAmount, setBetAmount] = useState<string>('');
  const { placeBet, isPending, isConfirming, isConfirmed, error } = usePlaceBet();

  const handlePrediction = () => {
    if (selectedOption === null || !betAmount) return;

    try {
      const value = parseEther(betAmount);
      placeBet(videoId, BigInt(selectedOption), value);
    } catch (err) {
      toast.error(`Error: ${err instanceof Error ? err.message : 'Invalid bet parameters'}`);
    }
  };

  useEffect(() => {
    if (isPending) {
      toast.loading('Confirm in your wallet...', { id: 'bet-tx' });
    } else if (isConfirming) {
      toast.loading('Waiting for confirmation...', { id: 'bet-tx' });
    } else if (isConfirmed) {
      toast.success('Bet placed successfully!', { id: 'bet-tx' });
      setBetAmount('');
      setSelectedOption(null);
    } else if (error) {
      toast.error(`Transaction failed`, { id: 'bet-tx' });
    }
  }, [isPending, isConfirming, isConfirmed, error]);




  return (
    <>
      <div className={`video-card video-card--${video.color}`}>
        <video
          className="video-group-card__thumbnail"
          controls
        >
          <source src={"https://" + video.videoUrl} type="video/mp4" />
        </video>
        <div className="video-card__content">
          {video.postUrl && (
            <a href={"https://" + video.postUrl || video.videoUrl} target="_blank" rel="noopener noreferrer"><p className='video-group-card__link'> Go to post <FiExternalLink size={14} /></p></a>
          )
          }
          {
            !video.postUrl && (
              <>
                <h3 className="video-card__question">{video.question}</h3>
                <p className="video-card__instruction">Select your prediction range</p>
                <div className="video-card__options">
                  {video.predictionOptions.map((option, index) => (
                    <button
                      key={index}
                      className={`prediction-option ${selectedOption === index ? 'prediction-option--selected' : ''
                        }`}
                      onClick={() => setSelectedOption(index)}
                      disabled={isPending}
                    >
                      <div className="prediction-option__range">{option.range}</div>
                    </button>
                  ))}
                </div>
              </>
            )
          }

          <div className="video-card__stats">
            <div className="video-card__stat">
              <span className="video-card__stat-icon"><TbMoneybag /></span>
              <div>
                <div className="video-card__stat-label">Total pot</div>
                <div className="video-card__stat-value">{video.totalPot}</div>
              </div>
            </div>
            <div className="video-card__stat">
              <FiUsers />
              <div>
                <div className="video-card__stat-label">Predictions</div>
                <div className="video-card__stat-value">{video.totalPredictions}</div>
              </div>
            </div>
          </div>

          <div className="video-card__footer">
            {isBettingClosed ? (
              <div className="video-card__closed-message">
                <div className="video-card__closes">
                  <FiClock />
                  <span>Views collection ends in:</span>
                  {video.viewsDeadline ? (
                    <Countdown targetTimestamp={video.viewsDeadline} />
                  ) : (
                    <span>Pending...</span>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="video-card__input-group">
                  <input
                    type="number"
                    className="video-card__amount-input"
                    placeholder="Amount (ETH)"
                    value={betAmount}
                    onChange={(e) => setBetAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    disabled={selectedOption === null || isPending}
                  />
                </div>

                <div className="video-card__closes">
                  <FiClock />
                  <span>Closes in </span>
                  {video.bettingDeadline ? (
                    <Countdown targetTimestamp={video.bettingDeadline} />
                  ) : (
                    <span>{video.closesIn}</span>
                  )}
                </div>
                <button
                  className={`btn btn-prediction btn-prediction--${video.color}`}
                  onClick={handlePrediction}
                  disabled={selectedOption === null || !betAmount || isPending || isConfirming}
                >
                  {isPending ? 'Confirm in Wallet...' : isConfirming ? 'Confirming...' : betAmount ? `Place ${betAmount} ETH` : 'Place Prediction'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

    </>
  );
}
